/**
 * Slice 1 invite screens (I2, I6, I7) — exact Claude Design.
 * Logic unchanged: validate token, accept member/cohost invite, route by role.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { storeInviteToken, clearInviteToken, isLiveInvite } from "@/lib/inviteTokenStorage";
import { setEmailVerifiedForUser } from "@/lib/emailVerification";
import { setViewContextFromInvite } from "@/contexts/ViewContext";
import { firstNameFromLabel } from "@/lib/workspaceMode";
import { useToast } from "@/hooks/use-toast";

interface InvitePropertyCard {
  id: string;
  name: string;
  address?: string | null;
  photo_url?: string | null;
}

interface InviteDetails {
  id: string;
  email: string;
  invite_role: string;
  property_id: string | null;
  property_name: string | null;
  property_address?: string | null;
  property_photo_url?: string | null;
  properties?: InvitePropertyCard[] | null;
  inviter_name: string | null;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  access_level: string | null;
  source: "member" | "cohost";
}

function initialOf(name: string) {
  const t = name.trim();
  return t ? t[0]!.toUpperCase() : "?";
}

const InvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading: authLoading, signOut, refetchProfile } = useAuth();
  const { is_owner, is_cohost } = useUserCapabilities();
  const { toast } = useToast();
  const rawToken = searchParams.get("token") ?? searchParams.get("invite_token");
  const token = rawToken != null && rawToken.trim() !== "" ? rawToken.trim() : null;

  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [inviteLoading, setInviteLoading] = useState(!!token);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [deadKind, setDeadKind] = useState<"expired" | "used" | "revoked" | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [hostCleanerConfirm, setHostCleanerConfirm] = useState<string | null>(null);
  const alreadyRedirected = useRef(false);
  const noTokenRedirected = useRef(false);

  const loginUrl = token ? `/login?invite_token=${encodeURIComponent(token)}` : "/login";
  const signUpUrl = token ? `/login?mode=signup&invite_token=${encodeURIComponent(token)}` : "/login";

  const goToSignIn = useCallback(() => {
    clearInviteToken();
    navigate("/login", { replace: true });
  }, [navigate]);

  const fetchInvite = useCallback(async (t: string): Promise<InviteDetails | null> => {
    const { data: memberData, error: memberError } = await supabase.rpc("get_member_invite_by_token", {
      p_token: t,
    });
    if (!memberError && memberData) {
      const row = memberData as Omit<InviteDetails, "source">;
      return { ...row, email: row.email, source: "member" };
    }

    const { data: cohostData, error: cohostError } = await supabase.rpc("get_cohost_invite_by_token", {
      p_token: t,
    });
    if (cohostError || !cohostData) return null;
    const c = cohostData as {
      id: string;
      invited_email: string;
      property_id: string;
      property_name: string | null;
      property_address?: string | null;
      property_photo_url?: string | null;
      inviter_name: string | null;
      accepted_at: string | null;
      access_level: string | null;
      expires_at?: string;
      revoked_at?: string | null;
    };
    return {
      id: c.id,
      email: c.invited_email,
      invite_role: "cohost",
      property_id: c.property_id,
      property_name: c.property_name,
      property_address: c.property_address ?? null,
      property_photo_url: c.property_photo_url ?? null,
      inviter_name: c.inviter_name,
      expires_at: c.expires_at ?? "",
      accepted_at: c.accepted_at,
      revoked_at: c.revoked_at ?? null,
      access_level: c.access_level,
      source: "cohost",
    };
  }, []);

  useEffect(() => {
    if (token || noTokenRedirected.current) return;
    noTokenRedirected.current = true;
    navigate("/", { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setInviteLoading(true);
      const inv = await fetchInvite(token);
      if (cancelled) return;
      if (!inv || !isLiveInvite(inv)) {
        clearInviteToken();
        if (!inv) {
          setInviteError("Invalid or expired invite. Ask the sender to resend.");
          setDeadKind("expired");
        } else if (inv.revoked_at) {
          setInviteDetails(inv);
          setInviteError("This invite has been revoked.");
          setDeadKind("revoked");
        } else if (inv.accepted_at) {
          setInviteDetails(inv);
          setDeadKind("used");
        } else {
          setInviteDetails(inv);
          setInviteError("This invite has expired. Ask the sender to resend.");
          setDeadKind("expired");
        }
        setInviteLoading(false);
        return;
      }
      storeInviteToken(token);
      setInviteDetails(inv);
      setInviteLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, fetchInvite]);

  const routeByRole = useCallback(
    (role: string, propertyId: string | null) => {
      const q = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
      if (role === "cleaner") {
        setViewContextFromInvite("cleaner");
        navigate(`/worker/schedule${q}`, { replace: true });
      } else {
        setViewContextFromInvite("owner");
        navigate(`/owner/properties${q}`, { replace: true });
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (!user || !inviteDetails?.accepted_at || alreadyRedirected.current) return;
    alreadyRedirected.current = true;
    routeByRole(inviteDetails.invite_role, inviteDetails.property_id);
  }, [user?.id, inviteDetails?.accepted_at, inviteDetails?.invite_role, inviteDetails?.property_id, routeByRole]);

  const acceptInvite = async () => {
    if (!token || !user || !inviteDetails) return;
    const userEmail = user.email?.toLowerCase().trim();
    if (!userEmail || userEmail !== (inviteDetails.email ?? "").toLowerCase()) return;
    setAccepting(true);
    try {
      if (inviteDetails.source === "cohost") {
        const { error } = await supabase.rpc("accept_cohost_invite", { p_token: token.trim() });
        if (error) {
          toast({ title: "Could not accept invite", description: error.message, variant: "destructive" });
          setAccepting(false);
          return;
        }
      } else {
        const { error } = await supabase.rpc("accept_member_invite", { p_token: token.trim() });
        if (error) {
          toast({ title: "Could not accept invite", description: error.message, variant: "destructive" });
          setAccepting(false);
          return;
        }
        if (inviteDetails.invite_role === "cleaner" && user.id) {
          setEmailVerifiedForUser(user.id);
        }
      }
      clearInviteToken();
      await refetchProfile();
      await queryClient.invalidateQueries({ queryKey: ["access-resolver"] });
      await queryClient.invalidateQueries({ queryKey: ["user-capabilities"] });
      await queryClient.invalidateQueries({ queryKey: ["cleaner"] });
      if (inviteDetails.invite_role === "cleaner" && (is_owner || is_cohost)) {
        setHostCleanerConfirm(firstNameFromLabel(inviteDetails.inviter_name));
        setViewContextFromInvite("cleaner");
        return;
      }
      routeByRole(inviteDetails.invite_role, inviteDetails.property_id);
    } catch {
      toast({ title: "Error", description: "Could not accept invite.", variant: "destructive" });
    } finally {
      setAccepting(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[#f4f7f9] p-4">
      <div className="flex w-full max-w-[390px] flex-col">{children}</div>
    </div>
  );

  if (!token) {
    return shell(<Loader2 className="mx-auto h-8 w-8 animate-spin text-[#94a3b8]" />);
  }

  if (inviteLoading) {
    return shell(<Loader2 className="mx-auto h-8 w-8 animate-spin text-[#24a89a]" />);
  }

  if (hostCleanerConfirm) {
    return shell(
      <div className="flex flex-col items-center justify-center gap-5 py-16 text-center">
        <div className="flex h-[74px] w-[74px] items-center justify-center rounded-[22px] bg-[#d7efec]">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#15776d" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12.5l5 5L20 6.5" />
          </svg>
        </div>
        <div className="text-[25px] font-bold leading-[1.25] tracking-[-0.5px] text-[#0f1720]">
          You’re also cleaning for {hostCleanerConfirm}
        </div>
        <div className="flex w-full flex-col gap-3">
          <Button
            type="button"
            className="h-14 rounded-[15px] bg-[#24a89a] text-[17px] font-bold text-white shadow-none hover:bg-[#1f958a]"
            onClick={() => {
              setViewContextFromInvite("cleaner");
              navigate("/worker/schedule", { replace: true });
            }}
          >
            Go to jobs
          </Button>
          <button
            type="button"
            className="flex min-h-12 items-center justify-center text-[16px] font-bold text-[#15776d]"
            onClick={() => {
              setViewContextFromInvite("owner");
              navigate("/owner/properties", { replace: true });
            }}
          >
            Stay on properties
          </button>
        </div>
      </div>,
    );
  }

  // I7 — expired / used / revoked
  if (deadKind) {
    const title =
      deadKind === "expired" ? "Invite expired" : deadKind === "revoked" ? "Invite revoked" : "Invite used";
    const sub =
      deadKind === "expired"
        ? "Ask them to resend."
        : deadKind === "revoked"
          ? "Ask them to resend."
          : "Sign in to continue.";
    return shell(
      <div className="flex flex-col items-center justify-center gap-[22px] py-16">
        <div className="flex h-[92px] w-[92px] items-center justify-center rounded-full bg-[#e7edf1]">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5V12l3 2" />
          </svg>
        </div>
        <div className="text-center text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">{title}</div>
        <div className="text-center text-[16px] font-medium leading-[1.45] text-[#64748b]">{sub}</div>
        <Button
          type="button"
          variant="outline"
          className="h-14 rounded-[15px] border-[1.5px] border-[#d7e0e6] bg-white px-8 text-[17px] font-bold text-[#0f1720] shadow-none hover:border-[#24a89a]"
          onClick={goToSignIn}
        >
          Sign in
        </Button>
      </div>,
    );
  }

  if (!inviteDetails) {
    return shell(
      <div className="flex flex-col items-center justify-center gap-[22px] py-16">
        <div className="text-center text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">Invite expired</div>
        <div className="text-center text-[16px] font-medium leading-[1.45] text-[#64748b]">Ask them to resend.</div>
        <Button
          type="button"
          variant="outline"
          className="h-14 rounded-[15px] border-[1.5px] border-[#d7e0e6] bg-white px-8 text-[17px] font-bold text-[#0f1720] shadow-none hover:border-[#24a89a]"
          onClick={goToSignIn}
        >
          Sign in
        </Button>
      </div>,
    );
  }

  const properties: InvitePropertyCard[] =
    Array.isArray(inviteDetails.properties) && inviteDetails.properties.length > 0
      ? inviteDetails.properties.map((p) => ({
          id: String(p.id),
          name: (p.name || "Property").trim() || "Property",
          address: p.address ?? null,
          photo_url: p.photo_url ?? null,
        }))
      : inviteDetails.property_id || inviteDetails.property_name
        ? [
            {
              id: inviteDetails.property_id || "property",
              name: inviteDetails.property_name || "Property",
              address: inviteDetails.property_address ?? null,
              photo_url: inviteDetails.property_photo_url ?? null,
            },
          ]
        : [];

  const inviterName = inviteDetails.inviter_name || "Someone";
  const inviterInitial = initialOf(inviterName);

  const inviteScopeLabel =
    properties.length === 0
      ? "their team"
      : properties.length === 1
        ? properties[0]!.name
        : `${properties.length} properties`;

  const propertyCards = (
    <div className="flex max-h-[min(42vh,320px)] flex-col gap-2.5 overflow-y-auto">
      {properties.map((p) => (
        <div
          key={p.id}
          className="flex items-center gap-3.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-4"
        >
          <div className="h-[52px] w-[52px] flex-none overflow-hidden rounded-xl border-[1.5px] border-[#e6edf1] bg-[#eef2f5]">
            {p.photo_url ? (
              <img src={p.photo_url} alt="" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <div className="truncate text-[17px] font-bold text-[#0f1720]">{p.name}</div>
            <div className="truncate text-[13px] font-medium text-[#64748b]">
              {p.address?.trim() || "Property"}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // I6 — wrong account
  if (user && !inviteDetails.accepted_at) {
    const userEmail = (user.email ?? "").toLowerCase().trim();
    const inviteEmail = (inviteDetails.email ?? "").toLowerCase();
    if (userEmail && inviteEmail && userEmail !== inviteEmail) {
      return shell(
        <div className="flex flex-col justify-center gap-[26px] py-10">
          <div className="mx-auto flex h-[66px] w-[66px] items-center justify-center rounded-full bg-[#fdeaea]">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 8v5" />
              <circle cx="12" cy="16.6" r="0.2" />
              <path d="M10.3 3.9L2.6 17.4A1.9 1.9 0 004.3 20.3h15.4a1.9 1.9 0 001.7-2.9L13.7 3.9a1.9 1.9 0 00-3.4 0z" />
            </svg>
          </div>
          <div className="text-center text-[26px] font-bold tracking-[-0.4px] text-[#0f1720]">Wrong account</div>
          <div className="overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white">
            <div className="flex items-center justify-between gap-3 px-[18px] py-[15px]">
              <div className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">SIGNED IN</div>
              <div className="truncate text-[15px] font-bold text-[#0f1720]">{user.email}</div>
            </div>
            <div className="h-[1.5px] bg-[#e6edf1]" />
            <div className="flex items-center justify-between gap-3 px-[18px] py-[15px]">
              <div className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">INVITE FOR</div>
              <div className="truncate text-[15px] font-bold text-[#24a89a]">{inviteDetails.email}</div>
            </div>
          </div>
          <Button
            type="button"
            className="h-[58px] rounded-[15px] bg-[#24a89a] text-[18px] font-bold text-white shadow-none hover:bg-[#1f958a]"
            onClick={async () => {
              await signOut();
              navigate(loginUrl, { replace: true });
            }}
          >
            Switch account
          </Button>
        </div>,
      );
    }

    // Logged in, email matches — Accept
    return shell(
      <div className="flex flex-col justify-center gap-8 py-10">
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-[66px] w-[66px] items-center justify-center rounded-full bg-[#24a89a] text-[26px] font-bold text-white">
            {inviterInitial}
          </div>
          <div className="text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">You’re invited</div>
          <div className="text-center text-[16px] font-medium leading-[1.45] text-[#64748b]">
            {inviterName} invited you to {inviteScopeLabel}
          </div>
        </div>
        {propertyCards}
        <Button
          type="button"
          disabled={accepting || authLoading}
          className="h-[58px] rounded-[15px] bg-[#24a89a] text-[18px] font-bold text-white shadow-none hover:bg-[#1f958a]"
          onClick={() => void acceptInvite()}
        >
          {accepting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Accept"}
        </Button>
      </div>,
    );
  }

  // I2 — logged out landing
  return shell(
    <div className="flex flex-col justify-center gap-[34px] py-10">
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-[66px] w-[66px] items-center justify-center rounded-full bg-[#24a89a] text-[26px] font-bold text-white">
          {inviterInitial}
        </div>
        <div className="text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">You’re invited</div>
        <div className="text-center text-[16px] font-medium leading-[1.45] text-[#64748b]">
          {inviterName} invited you to {inviteScopeLabel}
        </div>
      </div>
      {propertyCards}
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          className="h-[58px] rounded-[15px] bg-[#24a89a] text-[18px] font-bold text-white shadow-none hover:bg-[#1f958a]"
          onClick={() => navigate(signUpUrl)}
        >
          Create account
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-[58px] rounded-[15px] border-[1.5px] border-[#d7e0e6] bg-white text-[18px] font-bold text-[#0f1720] shadow-none hover:border-[#24a89a]"
          onClick={() => navigate(loginUrl)}
        >
          Sign in
        </Button>
      </div>
    </div>,
  );
};

export default InvitePage;
